import { getSexDropdownSource, createSexValidator } from '@/model/Animal.utils';
import { _isEmpty, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { SpeciesV1 } from '@/model/Species.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { MetadataField, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { PlannedAnimal, PlannedAnimalData, PlanTypes } from '@/model/PlannedAnimal.model';
import { PlanTypeMap } from '@/model/PlannedAnimal.model';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { CustomColumnSettingsV16 } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import { MultiSelectEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/MultiSelectEditorV16';
import { DateEditorWrapperV16 } from '@/components/UI/SpreadSheet/editors-v16/DateEditorWrapperV16';
import { defaultMetadataToColumnSettingMapperV16, formatColumnSettingsDataValueV16 } from '@/support/hot-v16';
import { updateStrainSelectDropdownV16 } from '@/components/Colony/Colony.utils.v16';
import { renderToStaticMarkup } from 'react-dom/server';
import { GroupCell, type GroupOption } from '@/components/Studies/Create/Steps/Animals/GroupCell';
import {
  validatePlannedAnimalId,
  validateGroupName,
  validateDob,
  validateSpecies,
  baseColHeaders,
  baseColHeadersWithDob,
  baseColHeadersWithOptionalDob,
  createPlannedAnimalData,
  type AnimalRow,
  type ValidationCache,
} from '@/components/Studies/Create/Steps/Animals/Animals.utils';
import { generateTempApiId, isTempApiId } from '@/utils/ApiId.utils';
import { DateUtils } from '@/utils/Date.utils';
import type Handsontable from 'handsontable-v16';
import type { AnimalSpecies } from '@/model/Animal.model';
import type { StrainApiId } from '@/model/Strain.model';
import type { UUID } from '@/model/Common.model';

const groupCellRendererV16 = (groupOptions: Map<string, GroupOption>) => {
  return (
    _instance: unknown,
    TD: HTMLTableCellElement,
    row: number,
    col: number,
    _prop: string | number,
    value: unknown
  ): void => {
    TD.innerHTML = '';
    const hot = _instance as { getCellMeta?: (r: number, c: number) => Record<string, unknown> };
    const cellMeta = hot.getCellMeta?.(row, col) ?? {};
    const readOnly = cellMeta.readOnly === true;
    TD.className = 'htMiddle';
    if (cellMeta.valid === false) {
      TD.classList.add('htInvalid');
    }
    if (readOnly) {
      TD.classList.add('htDimmed');
    }
    const container = document.createElement('div');
    container.classList.add('h-100', 'w-100');
    container.innerHTML = renderToStaticMarkup(
      <GroupCell option={groupOptions.get(((value as string) ?? '').trim())} readOnly={readOnly} />
    );
    TD.appendChild(container);
  };
};

export const getBaseColumnsV16 = (
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  species: Array<SpeciesV1>,
  includeDob: boolean = false,
  dobRequired: boolean = true,
  editGroups: boolean = true,
  planType: PlanTypes,
  unselectedRows: Array<PlannedAnimalDefinition> = [],
  includeUnknown: boolean = false,
  getValidationCache?: () => ValidationCache
): Array<CustomColumnSettingsV16> => {
  const groupOptions = new Map<string, GroupOption>(
    groups.map((group) => {
      const value = groupIdAccessor(group);
      return [value, { value, label: group.name, color: group.color }];
    })
  );

  function plannedIdValidator(
    this: { instance?: Handsontable.Core },
    value: string,
    callback: (valid: boolean) => void
  ): void {
    validatePlannedAnimalId(
      value,
      unselectedRows,
      callback,
      (this.instance ?? null) as unknown as Parameters<typeof validatePlannedAnimalId>[3],
      true,
      getValidationCache?.()
    );
  }

  function groupValidator(
    this: { instance?: Handsontable.Core },
    value: string,
    callback: (valid: boolean) => void
  ): void {
    validateGroupName(
      value,
      unselectedRows.filter((a) => !_notNil(a.animal_api_id)),
      callback,
      (this.instance ?? null) as unknown as Parameters<typeof validateGroupName>[3],
      groups,
      planType,
      groupIdAccessor,
      getValidationCache?.()
    );
  }

  function strainValidator(this: unknown, value: string, callback: (valid: boolean) => void): void {
    const hot = (this as { instance?: Handsontable.Core }).instance;
    const row = (this as { row?: number }).row;

    if (!_notNil(hot) || !_notNil(row)) {
      return callback(true);
    }

    const speciesCol = hot.propToCol('species');
    const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
    const hasSpecies = _notNil(selectedSpecies) && _isNotEmpty(selectedSpecies);
    const hasStrain = _notNil(value) && _isNotEmpty(value);

    if (!hasSpecies && !hasStrain) {
      return callback(true);
    }

    if (!hasSpecies && hasStrain) {
      return callback(false);
    }

    if (hasSpecies && !hasStrain) {
      return callback(false);
    }

    const validStrains = species.reduce<Array<string>>((acc, sp) => {
      if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
        (sp.strains as unknown as Array<{ name: string }>).forEach((strain) => {
          acc.push(strain.name);
        });
      }
      return acc;
    }, []);

    return callback(validStrains.includes(value));
  }

  const baseColumns: Array<CustomColumnSettingsV16> = [
    {
      data: 'plannedId',
      type: 'text',
      readOnly: false,
      allowInvalid: true,
      width: 150,
      validator: plannedIdValidator,
      validationErrorMessage: () => 'ID is required and must be unique.',
    },
    {
      data: 'groupIdentifier',
      type: MultiSelectEditorV16.type,
      isMultiSelect: false,
      optionsMap: groupOptions,
      readOnly: !editGroups,
      width: 200,
      validator: groupValidator,
      validationErrorMessage: (value: unknown) => {
        if (_isEmpty(value as string)) {
          return 'Planned animals must be assigned to a group';
        }
        const group = groups.find((g) => groupIdAccessor(g) === value);
        if (_notNil(group)) {
          if (editGroups) {
            return 'Number of planned animals assigned exceeds maximum subjects allowed: ' + (group.max_subjects ?? 0);
          }
          const availableCapacity = (group.max_subjects ?? 0) - (group.animals_count ?? 0);
          return 'Available group slots to register animals: ' + availableCapacity;
        }
        return 'Planned animals must be assigned to a group';
      },
      renderer: groupCellRendererV16(groupOptions),
    },
    {
      data: 'sex',
      type: 'dropdown',
      source: getSexDropdownSource(includeUnknown),
      allowInvalid: true,
      width: 110,
      validator: createSexValidator(includeUnknown),
    },
  ] as Array<CustomColumnSettingsV16>;

  if (includeDob) {
    baseColumns.push({
      data: 'dob',
      type: DateEditorWrapperV16.type,
      dateFormat: 'YYYY-MM-DD',
      width: 150,
      required: dobRequired,
      validator: function (value: string, callback: (valid: boolean) => void): void {
        validateDob(dobRequired, value, callback);
      },
      validationErrorMessage: () =>
        dobRequired
          ? 'Required in YYYY-MM-DD and must not be a future date.'
          : 'Must be in YYYY-MM-DD format and not a future date if provided.',
    } as CustomColumnSettingsV16);
  }

  baseColumns.push(
    {
      data: 'species',
      type: 'dropdown',
      width: 150,
      source: species.map((s) => s.name),
      validator: function (value: string, callback: (valid: boolean) => void): void {
        validateSpecies(value, callback, species);
      },
      linkedValidationColumns: ['strain'],
    } as CustomColumnSettingsV16,
    {
      data: 'strain',
      type: 'dropdown',
      width: 150,
      strict: true,
      validator: strainValidator,
      source(_query: string, process: (result: Array<string>) => void) {
        const hot = (this as { instance?: Handsontable.Core }).instance;
        const row = (this as { row?: number }).row;
        if (_notNil(hot) && _notNil(row)) {
          const speciesCol = hot.propToCol('species');
          const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
          const strains = species.reduce<Array<string>>((acc, sp) => {
            if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
              (sp.strains as unknown as Array<{ name: string }>).forEach((strain) => {
                acc.push(strain.name);
              });
            }
            return acc;
          }, []);
          process(strains);
        } else {
          process([]);
        }
      },
      validationErrorMessage: () => 'Strain is required when a Species is selected, otherwise it should be left blank.',
    } as CustomColumnSettingsV16
  );

  return baseColumns;
};

export const getColumnDataV16 = (
  species: Array<SpeciesV1>,
  selectedMetadata: Array<MetadataField>,
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  planType: PlanTypes,
  editGroups: boolean = true,
  dobRequired: boolean = true,
  unselectedRows: Array<PlannedAnimalDefinition> = [],
  includeUnknown: boolean = false,
  getValidationCache?: () => ValidationCache
): { colHeaders: Array<string>; columns: Array<CustomColumnSettingsV16> } => {
  const includeDob = planType === PlanTypeMap.registered;
  const baseColumns = getBaseColumnsV16(
    groups,
    groupIdAccessor,
    species,
    includeDob,
    dobRequired,
    editGroups,
    planType,
    unselectedRows,
    includeUnknown,
    getValidationCache
  );

  const colHeaders = includeDob
    ? dobRequired
      ? baseColHeadersWithDob
      : baseColHeadersWithOptionalDob
    : baseColHeaders;

  const metadataColumns = selectedMetadata.reduce<{
    headers: Array<string>;
    cols: Array<CustomColumnSettingsV16>;
  }>(
    (acc, metadata) => {
      const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapperV16(metadata);
      acc.headers.push(columnHeader);
      acc.cols.push(columnSettings as CustomColumnSettingsV16);
      return acc;
    },
    { headers: [], cols: [] }
  );

  return {
    colHeaders: [...colHeaders.names, ...metadataColumns.headers],
    columns: [...baseColumns, ...metadataColumns.cols],
  };
};

export const patchMetadataV16 = (hotInstance: Handsontable.Core | null, species: Array<SpeciesV1>): void => {
  if (_notNil(hotInstance) && !(hotInstance as unknown as { isDestroyed: boolean }).isDestroyed) {
    const col = hotInstance.propToCol('species');
    const colData = hotInstance.getDataAtCol(col as number);
    colData.forEach((value: unknown, row: number) => {
      updateStrainSelectDropdownV16(hotInstance, species as unknown as Array<AnimalSpecies>, row, value as string);
    });
    hotInstance.render();
  }
};

const isMultiSelectField = (field: MetadataField): boolean =>
  field.field_type === 'multi_select' || field.field_type === 'lookup_multi_select';

/**
 * Build an empty `meta` record keyed by v16 encoded titles so that Handsontable v16
 * column accessors (`meta.<encodedTitle>`) can read/write cell values without the row
 * being treated as missing a nested object.
 */
const buildEmptyMetaRecord = (selectedMetadata: Array<MetadataField>): Record<string, unknown> =>
  selectedMetadata.reduce<Record<string, unknown>>((acc, field) => {
    acc[formatColumnSettingsDataValueV16(field.title)] = '';
    return acc;
  }, {});

/**
 * Convert the v12-shaped `row.metadata[fieldId]` into the v16-shaped `row.meta[encodedTitle]`
 * representation so that a freshly rendered Handsontable v16 instance can display the
 * existing values.
 */
const metadataToMetaV16 = (
  metadataRecord: Record<string, MetadataFieldWithValue> | undefined,
  selectedMetadata: Array<MetadataField>
): Record<string, unknown> => {
  return selectedMetadata.reduce<Record<string, unknown>>((acc, field) => {
    const encodedKey = formatColumnSettingsDataValueV16(field.title);
    const existing = metadataRecord?.[field.id] ?? metadataRecord?.[field.glossary_id as unknown as string | number];
    if (_notNil(existing) && _notNil(existing.value)) {
      acc[encodedKey] =
        isMultiSelectField(field) && Array.isArray(existing.value) ? existing.value.join(',') : existing.value;
    } else {
      acc[encodedKey] = '';
    }
    return acc;
  }, {});
};

/**
 * v16-aware replacement for generateInitialAnimals. Seeds an empty `meta` bucket for
 * each selected metadata column so Handsontable v16 writes land in a predictable path.
 */
export const generateInitialAnimalsV16 = (
  groups: Array<TreatmentGroup>,
  dobRequired: boolean,
  selectedMetadata: Array<MetadataField>
): Array<AnimalRow> => {
  const baseMeta = buildEmptyMetaRecord(selectedMetadata);
  const animalData: Array<AnimalRow> = [];
  const sortedGroups = [...groups].sort((a, b) => a.no - b.no);
  sortedGroups.forEach((group) => {
    const maxSubjects = group.max_subjects;
    if (maxSubjects > 0 && _isNotBlank(group.name ?? '')) {
      for (let i = 1; i <= maxSubjects; i++) {
        animalData.push({
          apiId: generateTempApiId('adf'),
          plannedId: `${group.no}_${i}`,
          groupIdentifier: group.temp_id as UUID,
          sex: '',
          dob: dobRequired ? DateUtils.dateNow() : '',
          species: '',
          strain: '',
          metadata: {},
          meta: { ...baseMeta },
        });
      }
    }
  });
  return animalData;
};

/**
 * v16-aware replacement for generateAnimalColumns. Preserves v12 `metadata` (downstream
 * consumers still rely on it) and additionally populates `meta[encodedTitle]` so that
 * Handsontable v16 renders the existing values.
 */
export const generateAnimalColumnsV16 = (
  animals: Array<PlannedAnimal>,
  groups: Array<TreatmentGroup>,
  planType: PlanTypes,
  dobRequired: boolean,
  selectedMetadata: Array<MetadataField>
): Array<AnimalRow> => {
  if (planType === PlanTypeMap.skip) {
    return [];
  }
  return animals.map((animal) => {
    const plannedGroup = groups.find((group) => group.temp_id === animal.group_temp_id);
    return {
      apiId:
        _notNil(animal.temp_id) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.temp_id)
          ? animal.temp_id
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      plannedId: animal.name,
      groupIdentifier: plannedGroup?.temp_id as UUID,
      sex: animal.sex,
      dob: animal?.dob ?? (dobRequired ? DateUtils.dateNow() : ''),
      species: animal.species,
      strain: animal.strain,
      metadata: animal.metadata ?? {},
      meta: metadataToMetaV16(animal.metadata, selectedMetadata),
    };
  });
};

/**
 * v16-aware replacement for convertToPlannedAnimals. Reads metadata from
 * `row.meta[encodedTitle]` (where Handsontable v16 writes) and rebuilds the v12-shaped
 * `metadata: Record<fieldId, MetadataFieldWithValue>` map expected by downstream
 * consumers (Redux store, study create POST payload, etc.).
 */
export const convertToPlannedAnimalsV16 = (
  animalData: Array<AnimalRow>,
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>,
  animalsPerCage: number | null,
  planType: PlanTypes,
  groupIdAccessor: (group: TreatmentGroup) => string,
  selectedMetadata: Array<MetadataField>
): PlannedAnimalData => {
  const groupMap = new Map(groups.map((g) => [groupIdAccessor(g), g]));
  const strainApiIdMap = new Map<string, string>();
  for (const s of species) {
    if (_isNotEmpty(s.strains)) {
      for (const strain of s.strains!) {
        strainApiIdMap.set(`${s.name}|${strain.name}`, strain.api_id);
      }
    }
  }

  const metadataFieldInfo = selectedMetadata.map((field) => ({
    field,
    encodedKey: formatColumnSettingsDataValueV16(field.title),
    isMultiSelect: isMultiSelectField(field),
  }));

  const plannedAnimals: Array<PlannedAnimal> = animalData.map((animal) => {
    const plannedGroup = groupMap.get(animal.groupIdentifier as string);
    const strainApiId = strainApiIdMap.get(`${animal.species}|${animal.strain}`) ?? '';

    const metaRecord = animal.meta ?? {};
    const existingMetadata = animal.metadata ?? {};
    const rebuiltMetadata: Record<string, MetadataFieldWithValue> = {};

    for (const { field, encodedKey, isMultiSelect } of metadataFieldInfo) {
      const existingEntry =
        existingMetadata[field.id] ?? existingMetadata[field.glossary_id as unknown as string | number];
      const rawValue = metaRecord[encodedKey];
      let value: MetadataFieldWithValue['value'];

      if (_notNil(rawValue)) {
        if (isMultiSelect) {
          if (typeof rawValue === 'string') {
            value = _isNotBlank(rawValue) ? rawValue.split(',').map((v) => v.trim()) : [];
          } else if (Array.isArray(rawValue)) {
            value = rawValue as MetadataFieldWithValue['value'];
          } else {
            value = rawValue as MetadataFieldWithValue['value'];
          }
        } else {
          value = rawValue as MetadataFieldWithValue['value'];
        }
      } else {
        value = existingEntry?.value ?? null;
      }

      rebuiltMetadata[field.id] = {
        ...(existingEntry ?? {}),
        id: field.id,
        title: field.title,
        glossary_id: field.glossary_id,
        glossary_api_id: field.glossary_api_id,
        field_type: field.field_type,
        read_only: field.read_only,
        slug: field.slug,
        required: field.required,
        options: field.options,
        value,
      } as MetadataFieldWithValue;
    }

    Object.entries(existingMetadata).forEach(([key, entry]) => {
      if (!_notNil(rebuiltMetadata[key])) {
        rebuiltMetadata[key] = entry;
      }
    });

    return {
      temp_id:
        _notNil(animal.apiId) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.apiId)
          ? animal.apiId
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      name: animal.plannedId,
      group_temp_id: plannedGroup?.temp_id,
      sex: animal.sex,
      dob: animal?.dob ?? '',
      species: animal?.species,
      strain: animal?.strain,
      strainApiId: strainApiId as StrainApiId,
      metadata: rebuiltMetadata,
    };
  });

  return createPlannedAnimalData(plannedAnimals, planType, animalsPerCage);
};
